import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const useMiningDashboard = () => {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyMineId, setBusyMineId] = useState("");
  const [busyToolId, setBusyToolId] = useState("");
  const [claimBusy, setClaimBusy] = useState(false);
  const [smeltBusyKey, setSmeltBusyKey] = useState("");
  const [milestoneBusyKey, setMilestoneBusyKey] = useState("");
  const [smithingBusyKey, setSmithingBusyKey] = useState("");
  const [toolUpgradeBusyKey, setToolUpgradeBusyKey] = useState("");
  const [boostBusyKey, setBoostBusyKey] = useState("");
  const [projectBusyKey, setProjectBusyKey] = useState("");
  const [projectClaimBusy, setProjectClaimBusy] = useState(false);
  const [autoMineSaving, setAutoMineSaving] = useState(false);
  const [nowMs, setNowMs] = useState(Date.now());
  const sessionRefreshKeyRef = useRef("");
  const projectRefreshKeyRef = useRef("");

  const loadMining = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data: response } = await api.get("/mining");
      setData(response);
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { loadMining(); }, []);
  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!data?.current_session) {
      sessionRefreshKeyRef.current = "";
      return undefined;
    }
    if (!data?.current_session || data?.pending_claim) return undefined;
    const refreshKey = data.current_session.ends_at || "";
    const remaining = new Date(data.current_session.ends_at).getTime() - nowMs;
    if (remaining > 0 && remaining <= 1200 && sessionRefreshKeyRef.current !== refreshKey) {
      sessionRefreshKeyRef.current = refreshKey;
      loadMining({ silent: true });
    }
    return undefined;
  }, [data?.current_session, data?.pending_claim, nowMs]);

  useEffect(() => {
    if (!data?.smithing?.active_project) {
      projectRefreshKeyRef.current = "";
      return undefined;
    }
    const refreshKey = data.smithing.active_project.ends_at || "";
    const remaining = new Date(data.smithing.active_project.ends_at).getTime() - nowMs;
    if (remaining > 0 && remaining <= 1200 && projectRefreshKeyRef.current !== refreshKey) {
      projectRefreshKeyRef.current = refreshKey;
      loadMining({ silent: true });
    }
    return undefined;
  }, [data?.smithing?.active_project, nowMs]);

  const liveSession = useMemo(() => {
    if (!data?.current_session) return null;
    const endsAtMs = new Date(data.current_session.ends_at).getTime();
    return {
      ...data.current_session,
      time_remaining_seconds: Math.max(0, Math.ceil((endsAtMs - nowMs) / 1000)),
    };
  }, [data?.current_session, nowMs]);

  const liveSmithing = useMemo(() => {
    if (!data?.smithing) return null;
    const activeProject = data.smithing.active_project;
    if (!activeProject?.ends_at) return data.smithing;
    const endsAtMs = new Date(activeProject.ends_at).getTime();
    const remainingSeconds = Math.max(0, Math.ceil((endsAtMs - nowMs) / 1000));
    return {
      ...data.smithing,
      active_project: {
        ...activeProject,
        time_remaining_seconds: remainingSeconds,
        claimable: remainingSeconds <= 0,
      },
      projects: (data.smithing.projects || []).map((project) => project.active ? {
        ...project,
        claimable: remainingSeconds <= 0,
        time_remaining_seconds: remainingSeconds,
      } : project),
    };
  }, [data?.smithing, nowMs]);

  const handleStart = async (mineId) => {
    setBusyMineId(mineId);
    try {
      const { data: res } = await api.post("/mining/start", { mine_id: mineId });
      toast.success(`Mining session started at ${res.session.mine_name}.`);
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusyMineId("");
    }
  };

  const handleClaim = async () => {
    setClaimBusy(true);
    try {
      const { data: res } = await api.post("/mining/claim");
      setData((prev) => prev ? {
        ...prev,
        current_session: null,
        pending_claim: null,
        recent_results: res?.recent_results || prev.recent_results,
        discoveries: res?.discoveries || prev.discoveries,
        profile: res?.profile || prev.profile,
      } : prev);
      toast.success("Mining rewards claimed.");
      if (res?.claim?.discovery?.name) {
        toast.success(`Rare discovery secured: ${res.claim.discovery.name}.`);
      }
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setClaimBusy(false);
    }
  };

  const handlePurchaseTool = async (toolId) => {
    setBusyToolId(toolId);
    try {
      const { data: res } = await api.post("/mining/tool/purchase", { tool_id: toolId });
      toast.success(`${res.tool.name} purchased.`);
      await refreshUser?.();
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusyToolId("");
    }
  };

  const handleEquipTool = async (toolId) => {
    setBusyToolId(toolId);
    try {
      const { data: res } = await api.post("/mining/tool/equip", { tool_id: toolId });
      toast.success(`${res.tool.name} equipped.`);
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusyToolId("");
    }
  };

  const handleRepairTool = async (toolId, currency = "gold") => {
    setBusyToolId(toolId);
    try {
      const { data: res } = await api.post("/mining/tool/repair", { tool_id: toolId, currency });
      toast.success(`${res.tool.name} repaired.`);
      await refreshUser?.();
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusyToolId("");
    }
  };

  const handleSmelt = async (rarity, kind, batches) => {
    const key = `${rarity}-${kind}`;
    setSmeltBusyKey(key);
    try {
      const { data: res } = await api.post("/mining/smelt", { rarity, kind, batches });
      toast.success(`Created ${res.result.fragments_gained} ${rarity} ${kind} fragment${res.result.fragments_gained === 1 ? "" : "s"}.`);
      await refreshUser?.();
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSmeltBusyKey("");
    }
  };

  const handleClaimMilestone = async (discoveryId, milestone) => {
    const key = `${discoveryId}-${milestone}`;
    setMilestoneBusyKey(key);
    try {
      await api.post("/mining/discovery/claim-milestone", { discovery_id: discoveryId, milestone });
      toast.success("Discovery milestone claimed.");
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setMilestoneBusyKey("");
    }
  };

  const handleCraftSmithingMaterial = async (materialId, amount = 1) => {
    setSmithingBusyKey(materialId);
    try {
      const { data: res } = await api.post("/mining/smithing/craft", { material_id: materialId, amount });
      toast.success(`Crafted ${res.crafted.output_count} ${materialId.replaceAll("_", " ")}.`);
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setSmithingBusyKey("");
    }
  };

  const handleUpgradeTool = async (toolId, materialId = "reinforced_handle") => {
    setToolUpgradeBusyKey(toolId);
    try {
      await api.post("/mining/tool/upgrade", { tool_id: toolId, material_id: materialId });
      toast.success("Pickaxe upgraded.");
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setToolUpgradeBusyKey("");
    }
  };

  const handleActivateBoost = async (materialId) => {
    setBoostBusyKey(materialId);
    try {
      await api.post("/mining/boost/activate", { material_id: materialId });
      toast.success("Mining boost primed for your next session.");
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBoostBusyKey("");
    }
  };

  const handleStartProject = async (projectId) => {
    setProjectBusyKey(projectId);
    try {
      const { data: res } = await api.post("/mining/project/start", { project_id: projectId });
      toast.success(`${res.active_project.name} started.`);
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setProjectBusyKey("");
    }
  };

  const handleClaimProject = async () => {
    setProjectClaimBusy(true);
    try {
      const { data: res } = await api.post("/mining/project/claim");
      toast.success(`${res.claimed_project.name} completed.`);
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setProjectClaimBusy(false);
    }
  };

  const handleSaveAutoMine = async ({ enabled, mineId, energyThreshold }) => {
    setAutoMineSaving(true);
    try {
      const { data: res } = await api.post("/mining/auto-config", {
        enabled,
        mine_id: mineId,
        energy_threshold: energyThreshold,
      });
      setData((prev) => prev ? {
        ...prev,
        offline_auto_mine: res?.offline_auto_mine || prev.offline_auto_mine,
        offline_auto_report: res?.offline_auto_report ?? prev.offline_auto_report,
      } : prev);
      toast.success(enabled ? "Offline auto-mining updated." : "Offline auto-mining disabled.");
      await loadMining({ silent: true });
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setAutoMineSaving(false);
    }
  };

  return {
    data,
    loading,
    loadMining,
    liveSession,
    liveSmithing,
    busyMineId,
    busyToolId,
    claimBusy,
    smeltBusyKey,
    milestoneBusyKey,
    smithingBusyKey,
    toolUpgradeBusyKey,
    boostBusyKey,
    projectBusyKey,
    projectClaimBusy,
    autoMineSaving,
    handleStart,
    handleClaim,
    handlePurchaseTool,
    handleEquipTool,
    handleRepairTool,
    handleSmelt,
    handleClaimMilestone,
    handleCraftSmithingMaterial,
    handleUpgradeTool,
    handleActivateBoost,
    handleStartProject,
    handleClaimProject,
    handleSaveAutoMine,
  };
};